import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ReportEdit } from 'src/app/report/report-list/report-edit';
import { MessengerService } from './messenger.service';

@Injectable({
  providedIn: 'root'
})
export class ReportService implements OnDestroy
{
  public reportTypes = [
    { name: 'Metric', value: 'METRIC' },
    { name: 'Report', value: 'REPORT' }
  ];

  public columnDataTypes: Array<{ text: string; value: string }> = [
    { text: 'String', value: 'STRING' },
    { text: 'Numeric', value: 'NUMERIC' },
    { text: 'Logical', value: 'BOOLEAN' },
    { text: 'Date', value: 'DATE' },
    { text: 'Timestamp', value: 'TIMESTAMP' },
    { text: 'Decimal', value: 'DECIMAL' },
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private messengerService: MessengerService,
    private translate: TranslateService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  /**
   * Get report meta data.
   *
   * @param viewName View name.
   * @returns Meta data columns.
   */
  public getReportMeta(viewName: string, reportId: string = null): Observable<Array<any>>
  {
    const queryString = reportId ? `?reportId=${reportId}` : '';
    return this.http.get<Array<any>>(`/report/view/${viewName.toLowerCase()}/meta${queryString}`);
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get Report by id.
   *
   * @param id Report id
   */
  public getReportById(id: number): Observable<ReportEdit>
  {
    return this.http.get<ReportEdit>(`/report/${id}`);
  }

  /**
   * Delete a Report.
   *
   * @param id Report id
   */
  public deleteReport(id: number): Observable<ReportEdit>
  {
    return this.http.delete<ReportEdit>(`/report/${id}`);
  }

  /**
   * Save a Report.
   *
   * @param data Report to persit
   * @param isNew Indicate if this is a new or update
   */
  public saveReport(data: any, isNew: boolean, id?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/report', data);
    }
    else
    {
      return this.http.put(`/report/${id}`, data);
    }
  }

  /**
   * Get list of report views.
   */
  public getReportViews(viewTypes: string | 'METRIC' | 'REPORT'): Observable<Array<string>>
  {
    return this.http.get<Array<string>>(`/report/views?type=${viewTypes}`);
  }

  /**
   * Check if Report is in use
   *
   * @param id Report Id
   */
  public existsReport(id: number): Observable<any>
  {
    return this.http.get(`/report/inuse/${id}`);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    this.translate.get(['REPORT-TYPE-METRIC', 'REPORT-TYPE-REPORT']).subscribe({
      next: (res) =>
      {
        let index = 0;
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            this.reportTypes[index].name = res[key];
            index++;
          }
        }
      }
    });

    this.translate.get(['COLUMNS-DATA-TYPES']).subscribe((res: string) =>
    {
      const splitted = res['COLUMNS-DATA-TYPES'].split(',');
      for (let i = 0; i < splitted.length; i++)
      {
        if (this.columnDataTypes.length > i)
        {
          this.columnDataTypes[i].text = splitted[i];
        }
      }
    });
  }
}
